import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useUser } from "./UserInfoProvider";

interface RequestCounts {
  general: number;
  dining: number;
  reservation: number;
}

interface RequestCountContextType {
  counts: RequestCounts;
  incrementCount: (requestType: "general" | "dining" | "reservation") => void;
  resetCount: (requestType: "general" | "dining" | "reservation") => void;
  resetAllCounts: () => void;
}

const RequestCountContext = createContext<RequestCountContextType | undefined>(
  undefined
);

export const useRequestCounts = () => {
  const context = useContext(RequestCountContext);
  if (!context) {
    throw new Error(
      "useRequestCounts must be used within a RequestCountProvider"
    );
  }
  return context;
};

interface RequestCountProviderProps {
  children: React.ReactNode;
}

export const RequestCountProvider: React.FC<RequestCountProviderProps> = ({
  children,
}) => {
  const { platformUserId } = useUser();
  const [counts, setCounts] = useState<RequestCounts>({
    general: 0,
    dining: 0,
    reservation: 0,
  });

  // Load counts from localStorage on mount
  useEffect(() => {
    if (!platformUserId) return;

    try {
      const stored = localStorage.getItem(
        `selfserve:request_counts:${platformUserId}`
      );
      if (stored) {
        const parsedCounts = JSON.parse(stored);
        setCounts(parsedCounts);
      }
    } catch (error) {
      console.error("Error loading request counts from localStorage:", error);
    }
  }, [platformUserId]);

  // Save counts to localStorage whenever they change
  useEffect(() => {
    if (!platformUserId) return;

    try {
      localStorage.setItem(
        `selfserve:request_counts:${platformUserId}`,
        JSON.stringify(counts)
      );
    } catch (error) {
      console.error("Error saving request counts to localStorage:", error);
    }
  }, [counts, platformUserId]);

  const incrementCount = useCallback(
    (requestType: "general" | "dining" | "reservation") => {
      setCounts((prev) => ({
        ...prev,
        [requestType]: prev[requestType] + 1,
      }));
    },
    []
  );

  const resetCount = useCallback(
    (requestType: "general" | "dining" | "reservation") => {
      setCounts((prev) => ({
        ...prev,
        [requestType]: 0,
      }));
    },
    []
  );

  const resetAllCounts = useCallback(() => {
    setCounts({
      general: 0,
      dining: 0,
      reservation: 0,
    });
  }, []);

  // Listen for push notifications
  useEffect(() => {
    const handleNotification = (event: CustomEvent) => {
      const { notificationType, data } = event.detail;

      if (notificationType === "new_guest_request_web" && data?.requesttype) {
        const requestType = data.requesttype.toLowerCase();

        // Map request types to our count keys
        if (requestType === "general") {
          incrementCount("general");
        } else if (requestType === "dining") {
          incrementCount("dining");
        } else if (requestType === "reservation") {
          incrementCount("reservation");
        }
      }
    };

    const handleServiceWorkerMessage = (event: MessageEvent) => {
      if (event.data?.type === "requestNotification") {
        const customEvent = new CustomEvent("requestNotification", {
          detail: event.data,
        });
        window.dispatchEvent(customEvent);
      }
    };

    // Listen for custom notification events
    window.addEventListener(
      "requestNotification",
      handleNotification as EventListener
    );

    // Listen for service worker messages
    navigator.serviceWorker?.addEventListener(
      "message",
      handleServiceWorkerMessage
    );

    return () => {
      window.removeEventListener(
        "requestNotification",
        handleNotification as EventListener
      );
      navigator.serviceWorker?.removeEventListener(
        "message",
        handleServiceWorkerMessage
      );
    };
  }, [incrementCount]);

  const value: RequestCountContextType = {
    counts,
    incrementCount,
    resetCount,
    resetAllCounts,
  };

  return (
    <RequestCountContext.Provider value={value}>
      {children}
    </RequestCountContext.Provider>
  );
};
